import {isEmpty, map} from "lodash";

import {convertToLocale} from "../../lib/utils";
import CONSTANT from "./constant";
import moment from "moment";

// Defensive: unknown sms type → fallback rendering instead of crash.
// Backend can emit message-type strings we don't have in the map yet
// (e.g. trial-mode "general" wallets pre-stage.201, or any new type added
// server-side before this constant catches up). Crashing the whole
// dashboard for a missing label is worse than rendering the raw key.
const SMS_TYPE_FALLBACK_COLOR = '#9e9e9e'
const smsTypeMeta = (el) => CONSTANT.SMS_TYPE_MAP[el] || {name: el, color: SMS_TYPE_FALLBACK_COLOR}

export const getSMSBalance = ({smsTypeTaken, smsCount}) => smsTypeTaken.map((el, index) => {
  const meta = smsTypeMeta(el)
  const count = smsCount?.[el] ?? 0
  return {
    id: index,
    value: count,
    label: `${meta.name} - ${convertToLocale(count)}`,
    color: meta.color
  }
})


const getDays = (key, selectedDuration) => {
  const original = key
  const keySplit = key.split('::')
  
  // Handle new API format with prefixes (day::, hour::, month::)
  // New format: day::12::Dec::2025, hour::22::346::2025, month::12::Dec::2025
  // Old format: 1::Dec::2025, 22::346::2025
  
  if (selectedDuration === 'days') {
    // New format: day::12::Dec::2025 -> "12 Dec"
    // Old format: 1::Dec::2025 -> "1 Dec"
    if (keySplit[0] === 'day') {
      return {key: keySplit.slice(1, 3).join(' '), original}
    }
    return {key: keySplit.slice(0, 2).join(' '), original}
  }
  
  if (selectedDuration === 'months') {
    // New format: month::12::Dec::2025 -> "Dec"
    // Old format: 12::Dec::2025 -> "Dec"
    if (keySplit[0] === 'month') {
      return {key: keySplit[2], original}
    }
    return {key: keySplit[1], original}
  }
  
  if (selectedDuration === 'hours') {
    // Formats: "hour::13::137::2026" (new) or "13::137::2026" (old)
    // hour is already in the caller's tz (0..23). dayOfYear + year are needed
    // for sort order so yesterday's hours don't collide with today's.
    const hasPrefix = keySplit[0] === 'hour'
    const hourValue = parseInt(hasPrefix ? keySplit[1] : keySplit[0])
    const dayOfYear = parseInt(hasPrefix ? keySplit[2] : keySplit[1])
    const year = parseInt(hasPrefix ? keySplit[3] : keySplit[2])
    const hh = hourValue % 12 || 12
    const ampm = hourValue < 12 ? 'AM' : 'PM'
    return {
      key: `${String(hh).padStart(2, '0')} ${ampm}`,
      original: year * 1000000 + dayOfYear * 100 + hourValue
    }
  }
  
  return {key, original}
}

// Parse a raw hour key from the API.
//   "hour::13::137::2026"  → {hour: 13, dayOfYear: 137, year: 2026}
//   "13::137::2026"        → {hour: 13, dayOfYear: 137, year: 2026}
const parseHourKey = (key) => {
  const parts = key.split('::')
  const hasPrefix = parts[0] === 'hour'
  return {
    hour: parseInt(hasPrefix ? parts[1] : parts[0]),
    dayOfYear: parseInt(hasPrefix ? parts[2] : parts[1]),
    year: parseInt(hasPrefix ? parts[3] : parts[2])
  }
}

// 0 → "12 AM", 1 → "01 AM", 12 → "12 PM", 13 → "01 PM", ...
const formatHourLabel = (hour) => {
  const hh = hour % 12 || 12
  const ampm = hour < 12 ? 'AM' : 'PM'
  return `${String(hh).padStart(2, '0')} ${ampm}`
}

// Treat an empty/missing filter list as "match everything" rather than
// "match nothing". Without this guard, a sms_config 401 (or any failure that
// leaves smsType/senderId as []) silently zeros out the entire chart while
// the dashboard API itself returns full data — the bars render blank even
// though traffic is healthy.
const matchesFilter = (list, name) =>
  !Array.isArray(list) || list.length === 0 || list.includes(name)

const aggregateSubmittedDelivered = (value, smsType, senderId) => {
  let submitted = 0
  value?.sent?.messagetype?.forEach(el => {
    if (matchesFilter(smsType, el?.name)) {
      el?.breakup?.forEach(innerEl => {
        if (matchesFilter(senderId, innerEl?.name)) {
          submitted += parseInt(innerEl.total)
        }
      })
    }
  })
  let delivered = 0
  value?.received?.messagetypeSenderid?.forEach(el => {
    if (matchesFilter(smsType, el?.name)) {
      el?.breakup?.forEach(innerEl => {
        if (matchesFilter(senderId, innerEl?.name)) {
          const deliverySuccess = innerEl.breakup?.find(item => item.name === 'DELIVERY_SUCCESS')
          delivered += parseInt(deliverySuccess?.total ?? 0)
        }
      })
    }
  })
  if (delivered > submitted) delivered = submitted
  return {submitted, delivered}
}

export const getSMSUsage = (data, selectedDuration, smsType, senderId) => {
  // "Today" view: render 12 AM → current-hour timeline for today only.
  // Future hours (e.g. 4 PM when it's 3 PM) are deliberately omitted so the
  // axis doesn't trail off into empty space. Past hours with no data still
  // appear as zero-height bars to keep the 1-hour spacing uniform — the
  // bug we hit was 01 AM ↔ 01 PM rendering adjacent because the band axis
  // only created slots for hours that had data, hiding the 12-hour gap.
  // Today filter (dayOfYear + year) prevents yesterday's afternoon from
  // bleeding in when the API window spans a calendar boundary.
  if (selectedDuration === 'hours') {
    const hoursData = data?.hours
    if (isEmpty(hoursData)) return []
    const now = moment()
    const todayDoy = now.dayOfYear()
    const todayYear = now.year()
    const currentHour = now.hour()
    const byHour = new Map()
    Object.entries(hoursData).forEach(([key, value]) => {
      const parsed = parseHourKey(key)
      if (parsed.year !== todayYear || parsed.dayOfYear !== todayDoy) return
      byHour.set(parsed.hour, aggregateSubmittedDelivered(value, smsType, senderId))
    })
    const filled = []
    for (let h = 0; h <= currentHour; h++) {
      const cell = byHour.get(h) || {submitted: 0, delivered: 0}
      filled.push({name: formatHourLabel(h), submitted: cell.submitted, delivered: cell.delivered})
    }
    return filled
  }

  let result = !isEmpty(data?.[selectedDuration]) ? map(data?.[selectedDuration], (value, key) => {
    const {submitted, delivered} = aggregateSubmittedDelivered(value, smsType, senderId)
    const formatted_key = getDays(key, selectedDuration)
    return {
      name: formatted_key.key,
      original: formatted_key.original,
      submitted,
      delivered
    }
  })
    : []
  result = result.sort((a, b) => moment(a.name).diff(b.name))
  if (selectedDuration === 'months') {
    return result.sort((a, b) => moment(a.original).diff(b.original))
  }
  return result.sort((a, b) => moment(a.name).diff(b.name))
}
